
import React, { useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { ArrowLeft, QrCode, Download, RefreshCw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const AtmQrCode: React.FC = () => {
  const { setCurrentScreen, getCurrentTransaction, updateTransactionStatus } = useApp();
  const { toast } = useToast();
  const transaction = getCurrentTransaction();

  useEffect(() => {
    if (!transaction) {
      setCurrentScreen('dashboard');
      return;
    }

    if (transaction.status === 'pending') {
      setCurrentScreen('transaction-confirmation');
    }
  }, [transaction, setCurrentScreen]);

  const handleDownload = () => {
    toast({
      title: "QR Code saved",
      description: "The QR code has been saved to your device",
    });
  };

  const handleCompleteTransaction = () => {
    if (transaction) {
      updateTransactionStatus(transaction.id, 'completed');
      toast({
        title: "Transaction completed",
        description: "Your transaction has been successfully completed",
      });
      setCurrentScreen('dashboard');
    }
  };

  if (!transaction) {
    return null;
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center mb-6 pt-4">
        <button 
          onClick={() => setCurrentScreen('dashboard')}
          className="p-2 rounded-full hover:bg-gray-100"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h1 className="text-2xl font-bold ml-4">ATM Withdrawal</h1>
      </div>

      <div className="flex-1 px-4 flex flex-col items-center">
        <div className="mobile-card w-full mb-6 text-center">
          <QrCode className="w-16 h-16 mx-auto mb-4 text-neobank-primary" />
          <h2 className="text-lg font-bold mb-2">Your QR Code is Ready</h2>
          <p className="text-gray-600 mb-6">
            Use this QR code at any supported ATM to withdraw ${parseFloat(transaction.amount) - 2} in cash.
          </p>
          
          <div className="bg-gray-50 p-6 rounded-lg mb-6 flex justify-center">
            {/* This would be a real QR code in a production app */}
            <div className="bg-white p-2 border border-gray-200">
              <img 
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${transaction.id}`} 
                alt="ATM QR Code" 
                className="w-56 h-56"
              />
            </div>
          </div>
          
          <button 
            onClick={handleDownload} 
            className="btn-outline flex items-center justify-center mb-3"
          >
            <Download className="w-4 h-4 mr-2" /> Save QR Code
          </button>
          
          <div className="text-sm text-gray-600">
            <p>QR code expires in 24 hours</p>
          </div>
        </div>

        <div className="mobile-card w-full mb-6">
          <h3 className="font-medium mb-3">Transaction Details</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Amount:</span>
              <span className="font-medium">${parseFloat(transaction.amount) - 2} USD</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Crypto Sent:</span>
              <span className="font-medium">{transaction.cryptoAmount} {transaction.cryptoCurrency}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Fee:</span>
              <span className="font-medium">$2.00</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Status:</span>
              <span className="px-2 py-1 rounded-full bg-green-100 text-green-800 text-xs font-medium">
                Ready for ATM
              </span>
            </div>
          </div>
        </div>

        <div className="mobile-card w-full mb-6">
          <h3 className="font-medium mb-3">ATM Instructions</h3>
          <ol className="pl-4 space-y-2 text-sm text-gray-600">
            <li>Go to any supported ATM location</li>
            <li>Select "QR Withdrawal" on the ATM screen</li>
            <li>Scan the QR code shown above</li>
            <li>Collect your cash from the ATM</li>
          </ol>
        </div>

        {/* For the purpose of this demo, we'll simulate the ATM withdrawal */}
        <p className="text-center text-sm text-gray-500 mb-4">
          For demo purposes, click the button below to simulate ATM withdrawal:
        </p>
        
        <button 
          onClick={handleCompleteTransaction} 
          className="btn-primary flex items-center justify-center text-lg py-4 mb-24"
        >
          <RefreshCw className="w-5 h-5 mr-2" /> Simulate ATM Withdrawal
        </button>
      </div>
    </div>
  );
};

export default AtmQrCode;
